import React, { useState, useEffect } from "react";
import { Box, Button, FormControl, InputLabel, MenuItem, Select, TextField, Typography, Paper, Grid, Alert, Autocomplete, InputAdornment } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { apiService } from "../../utils/api";
import { addMonths } from "date-fns";

const AddSubscription = () => {
  const [students, setStudents] = useState([]);
  const [plans, setPlans] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState("");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [amount, setAmount] = useState("");
  const [status, setStatus] = useState("active");
  const [message, setMessage] = useState({ type: "", text: "" });

  // Fetch students list
  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const response = await apiService.getStudents();
        setStudents(response.data.students);
      } catch (error) {
        console.error("Error fetching students:", error);
        setMessage({
          type: "error",
          text: "Error fetching students list",
        });
      }
    };
    fetchStudents();
  }, []);

  // Fetch plans
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await apiService.plans();

        if (response.data.success) {
          setPlans(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching plans:", error);
        setMessage({
          type: "error",
          text: "Error fetching subscription plans",
        });
      }
    };
    fetchPlans();
  }, []);

  // Update amount and end date when plan changes
  useEffect(() => {
    if (selectedPlan) {
      setAmount(selectedPlan.price.toString());
      // Set end date based on plan duration
      const newEndDate = addMonths(startDate, Number(selectedPlan.duration));
      setEndDate(newEndDate);
    }
  }, [selectedPlan, startDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const subscriptionData = {
        studentId: selectedStudent,
        planId: selectedPlan.id,
        planType: selectedPlan.type,
        amount: parseFloat(amount),
        startDate,
        endDate,
        status,
      };

      const response = await apiService.AddSubscription(subscriptionData);

      setMessage({
        type: "success",
        text: response.data.message,
      });

      // Reset form
      setSelectedStudent("");
      setSelectedPlan(null);
      setStartDate(new Date());
      setEndDate(new Date());
      setStatus("active");
    } catch (error) {
      console.error("Error adding subscription:", error);
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Error adding subscription",
      });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Add Manual Subscription
      </Typography>

      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item size={{ xs: 12, md: 4 }}>
              <Autocomplete
                fullWidth
                options={students}
                getOptionLabel={(student) => `${student.username} (${student.rollNo || "No Roll No"})`}
                value={students.find((student) => student.id === selectedStudent) || null}
                onChange={(event, newValue) => setSelectedStudent(newValue?.id)}
                renderInput={(params) => <TextField {...params} label="Select Student" required />}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
              />
            </Grid>

            <Grid item size={{ xs: 12, md: 4 }}>
              <Autocomplete
                fullWidth
                options={plans}
                getOptionLabel={(plan) => `${plan.name} (${plan.duration} months)`}
                value={selectedPlan}
                onChange={(event, newValue) => setSelectedPlan(newValue)}
                renderInput={(params) => <TextField {...params} label="Select Plan" required />}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
              />
            </Grid>

            <Grid item size={{ xs: 12, md: 4 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker label="Start Date" value={startDate} onChange={(newValue) => setStartDate(newValue)} renderInput={(params) => <TextField {...params} fullWidth required />} />
              </LocalizationProvider>
            </Grid>

            <Grid item size={{ xs: 12, md: 4 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker label="End Date" value={endDate} onChange={(newValue) => setEndDate(newValue)} renderInput={(params) => <TextField {...params} fullWidth required />} />
              </LocalizationProvider>
            </Grid>

            <Grid item size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Amount"
                value={amount}
                type="number"
                required
                InputProps={{
                  readOnly: true,
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
              />
            </Grid>

            <Grid item size={{ xs: 12, md: 4 }}>
              <Button type="submit" variant="contained" color="primary" size="large" fullWidth disabled={!selectedStudent || !selectedPlan}>
                Add Subscription
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default AddSubscription;
